import type { AdminAppealDetailDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { RedactedField } from '../../../components/pii';
import { tokens } from '../../../design';
import { formatCurrency } from '../../../utils/format';

/**
 * Appeal "context of the dispute" grid for the detail drawer (AUT-130,
 * architect 🟠 #5). Two-column info grid mirroring the mockup's
 * `.info-grid`:
 *   - Заказчик (filing customer) — PII-gated name.
 *   - Инспектор (accused inspector) — PII-gated name.
 *   - Заказ (order number).
 *   - Причина апелляции (reason).
 *   - Авто (vehicle info).
 *   - Сумма заказа (order amount).
 *
 * DUAL PII: BOTH `customer.name` and `inspector.name` render via
 * `<RedactedField perm="view_pii">`, governed by the SINGLE
 * `detail.piiRevealed` flag. A revealed value is never re-redacted; only
 * revealed values get a copy button. Names ONLY — the wire carries no
 * phone / email (don't invent).
 */

/** One label/value cell. */
const InfoItem = ({ label, children }: { label: string; children: ReactNode }): ReactElement => (
  <Box>
    <Typography variant="caption" sx={{ color: tokens.text.tertiary, display: 'block', mb: 0.25 }}>
      {label}
    </Typography>
    <Box sx={{ fontSize: 14, fontWeight: 500 }}>{children}</Box>
  </Box>
);

/** A PII-gated party name (customer / inspector), or a no-value dash. */
const PartyName = ({
  name,
  revealed,
}: {
  name: string | null;
  revealed: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  if (!name) {
    return (
      <Typography variant="body2" sx={{ color: tokens.text.tertiary }}>
        {t('appeals.detail.noValue')}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" component="span">
      <RedactedField
        perm="view_pii"
        value={name}
        redacted={name}
        {...(revealed ? { copyable: true } : {})}
      />
    </Typography>
  );
};

export const AppealContextGrid = ({ detail }: { detail: AdminAppealDetailDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          fontWeight: 600,
          color: tokens.text.tertiary,
          display: 'block',
          mb: 1.25,
        }}
      >
        {t('appeals.detail.contextTitle')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          rowGap: 1.75,
          columnGap: 3,
        }}
      >
        <InfoItem label={t('appeals.detail.fields.customer')}>
          <PartyName name={detail.customer.name} revealed={detail.piiRevealed} />
        </InfoItem>
        <InfoItem label={t('appeals.detail.fields.inspector')}>
          <PartyName name={detail.inspector.name} revealed={detail.piiRevealed} />
        </InfoItem>
        <InfoItem label={t('appeals.detail.fields.order')}>
          <Typography variant="body2" fontFamily="monospace">
            {`№ ${detail.order.orderNumber}`}
          </Typography>
        </InfoItem>
        <InfoItem label={t('appeals.detail.fields.reason')}>
          <Typography variant="body2">{t(`appeals.reason.${detail.reason}`)}</Typography>
        </InfoItem>
        <InfoItem label={t('appeals.detail.fields.vehicle')}>
          <Typography variant="body2">{detail.vehicleInfo}</Typography>
        </InfoItem>
        <InfoItem label={t('appeals.detail.fields.orderAmount')}>
          <Typography variant="body2" fontFamily="monospace">
            {formatCurrency(Number(detail.orderAmount))}
          </Typography>
        </InfoItem>
      </Box>
      <Box sx={{ mt: 1.75 }}>
        <InfoItem label={t('appeals.detail.fields.description')}>
          <Typography variant="body2" sx={{ color: tokens.text.secondary, whiteSpace: 'pre-wrap' }}>
            {detail.description}
          </Typography>
        </InfoItem>
      </Box>
    </Box>
  );
};
